import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Eye, EyeOff } from 'lucide-react';

/**
 * CoRegulate Input Components
 * 
 * Rounded corners (8-12px)
 * Background: Warm Oat (#FBE0C3)
 * Border: Soft, low-contrast Warm Grey (#7D8E95)
 * Focus: gentle glow or soft border shift
 */

/**
 * Brand Input
 * Single-line text input with gentle styling
 * Supports password visibility toggle when type="password"
 */
export const BrandInput = React.forwardRef(({
  className,
  type = 'text',
  label,
  error,
  hint,
  icon,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordField = type === 'password';
  const inputType = isPasswordField ? (showPassword ? 'text' : 'password') : type;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-[#1D6B6B] mb-2 font-body">
          {label}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#2A8585]">
            {icon}
          </div>
        )}
        <input
          ref={ref}
          type={inputType}
          className={cn(
            'w-full h-12 px-4 rounded-xl',
            'bg-[#FBE0C3]',
            'border-2 border-transparent',
            'text-[#1D6B6B] placeholder:text-[#2A8585]/60',
            'font-body text-base',
            'transition-all duration-400 ease-out',
            'focus:outline-none focus:border-[rgba(52,70,72,0.5)] focus:bg-[#FEF6EF]',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            icon && 'pl-12',
            isPasswordField && 'pr-12',
            error && 'border-[#FFBB98] focus:border-[#FFBB98]',
            className
          )}
          {...props}
        />
        {isPasswordField && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#2A8585] hover:text-[#1D6B6B] transition-colors focus:outline-none"
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            data-testid="toggle-password-visibility"
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        )}
      </div>
      {hint && !error && (
        <p className="mt-2 text-sm text-[#2A8585]">{hint}</p>
      )}
      {error && (
        <p className="mt-2 text-sm text-[#FFBB98]">{error}</p>
      )}
    </div>
  );
});
BrandInput.displayName = 'BrandInput';

/**
 * Brand Textarea
 * Multi-line text input with gentle styling
 */
export const BrandTextarea = React.forwardRef(({
  className,
  label,
  error,
  hint,
  rows = 4,
  ...props
}, ref) => {
  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-[#1D6B6B] mb-2 font-body">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        rows={rows}
        className={cn(
          'w-full px-4 py-3 rounded-xl',
          'bg-[#FBE0C3]',
          'border-2 border-transparent',
          'text-[#1D6B6B] placeholder:text-[#2A8585]/60',
          'font-body text-base leading-relaxed',
          'transition-all duration-400 ease-out',
          'focus:outline-none focus:border-[rgba(52,70,72,0.5)] focus:bg-[#FEF6EF]',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'resize-none',
          error && 'border-[#FFBB98] focus:border-[#FFBB98]',
          className
        )}
        {...props}
      />
      {hint && !error && (
        <p className="mt-2 text-sm text-[#2A8585]">{hint}</p>
      )}
      {error && (
        <p className="mt-2 text-sm text-[#FFBB98]">{error}</p>
      )}
    </div>
  );
});
BrandTextarea.displayName = 'BrandTextarea';

/**
 * Brand Select
 * Dropdown select with gentle styling
 */
export const BrandSelect = React.forwardRef(({
  className,
  label,
  error,
  hint,
  options = [],
  placeholder = 'Select an option',
  icon,
  ...props
}, ref) => {
  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-[#1D6B6B] mb-2 font-body">
          {label}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#2A8585] pointer-events-none">
            {icon}
          </div>
        )}
        <select
          ref={ref}
          className={cn(
            'w-full h-12 px-4 rounded-xl appearance-none',
            'bg-[#FBE0C3]',
            'border-2 border-transparent',
            'text-[#1D6B6B]',
            'font-body text-base',
            'transition-all duration-400 ease-out',
            'focus:outline-none focus:border-[rgba(52,70,72,0.5)] focus:bg-[#FEF6EF]',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'cursor-pointer',
            icon && 'pl-12',
            error && 'border-[#FFBB98] focus:border-[#FFBB98]',
            className
          )}
          {...props}
        >
          <option value="" disabled className="text-[#2A8585]/60">
            {placeholder}
          </option>
          {options.map((option) => (
            <option 
              key={option.value} 
              value={option.value}
              className="text-[#1D6B6B] bg-[#FBE0C3]"
            >
              {option.label}
            </option>
          ))}
        </select>
        {/* Custom dropdown arrow */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 16 16" 
            fill="none" 
            className="text-[#2A8585]"
          >
            <path 
              d="M4 6L8 10L12 6" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
      {hint && !error && (
        <p className="mt-2 text-sm text-[#2A8585]">{hint}</p>
      )}
      {error && (
        <p className="mt-2 text-sm text-[#FFBB98]">{error}</p>
      )}
    </div>
  );
});
BrandSelect.displayName = 'BrandSelect';

/**
 * Search Input
 * Input with search icon built in
 */
export const SearchInput = React.forwardRef(({
  className,
  placeholder = 'Search...',
  ...props
}, ref) => {
  return (
    <div className="relative">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#2A8585]">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      </div>
      <input
        ref={ref}
        type="search"
        placeholder={placeholder}
        className={cn(
          'w-full h-12 pl-12 pr-4 rounded-full',
          'bg-[#FBE0C3]',
          'border-2 border-transparent',
          'text-[#1D6B6B] placeholder:text-[#2A8585]/60',
          'font-body text-base',
          'transition-all duration-400 ease-out',
          'focus:outline-none focus:border-[rgba(52,70,72,0.5)] focus:bg-[#FEF6EF]',
          className
        )}
        {...props}
      />
    </div>
  );
});
SearchInput.displayName = 'SearchInput';

export default BrandInput;
